import { ActionIconDef, GridConfig, GridExportLimit, GridRow } from '@inavista/components/grid';
import { environment } from 'projects/service/src/environments/environment';
import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { serviceOrderStatuses, CallGroups } from '../../models/service-order.model';
import { centreStatusColumns } from 'projects/service/src/app/shared/utils/grid-status-align';

// Service Orders main grid on @inavista/components. Same columns, labels, default visibility,
// status colours and action-icon rules as the Syncfusion grid it replaces. The three tabs
// (Assigned / Completed / Canceled) are now system views in the grid's Views picker, stored in the database.

const S = serviceOrderStatuses;

export const ASSIGNED_STATUSES = [
  S.open, S.scheduled, S.paused, S.awaitingPayment, S.pendingApproval, S.estimateApproved,
  S.estimateRejected, S.enroute, S.inProgress, S.exportPublishError, S.importPublishError,
];
export const COMPLETED_STATUSES = [S.completed, S.costed];

// Same colours as today's pills (src/styles.scss .blue, .grey, .orange, ...).
const STATUS_COLORS: Record<string, string> = {
  'in progress': '#199efd',
  'open': '#656363',
  'scheduled': '#f3ae4b',
  'pending approval': '#f3ae4b',
  'completed': '#479e47',
  'estimate approved': '#479e47',
  'canceled': '#9e1414',
  'cancelled': '#9e1414',
  'interrupted': '#9e1414',
  'estimate rejected': '#9e1414',
  'en route': '#9399ff',
  'paused': '#ca7671',
  'awaiting payment': '#f7a56c',
};
export const statusColor = (value: unknown) => STATUS_COLORS[String(value ?? '').toLowerCase()] ?? '#656363';

// Everything the per-row rules need from the page; the page keeps it up to date.
export interface ServiceOrderGridContext {
  roleClaims: Record<string, { visible?: boolean; readOnly?: boolean }>;
  isKingslynn: boolean;
  isManagerLike: boolean;          // service managers, ISC manager, ISC supervisor
  showSOType: boolean;
  hasCheckedInServiceOrder: boolean;
  hasEnrouteServiceOrder: boolean;
  defaultLinkColor: string;
  // Export prompt numbers from the application parameters (DB-006); undefined = no prompt, loaded rows only.
  exportLimit?: GridExportLimit;
}

const BLACK = '#000000de';
const GREEN = '#5cb85c';
const ORANGE = '#ff8403';
const INACTIVE = '#cccccc';

const status = (row: GridRow) => row['status'] as string;
const callGroup = (row: GridRow) => (row['callgroup'] ?? row['callGroup']) as string;

// Why an action is not available for this row, or null when it is. The icon is greyed out,
// and a click says why instead of doing nothing (the user never guesses).
export function disabledReason(key: string, row: GridRow, ctx: ServiceOrderGridContext): string | null {
  const st = status(row);
  switch (key) {
    case 'assignments':
      return st === S.open ? 'Assignments are available once the order is scheduled.'
        : callGroup(row) === CallGroups.initialCalibration ? 'Initial calibration orders have no assignments.' : null;
    case 'nte':
      if (row['nteAmount']) return null;
      if ([S.completed, S.cancelled, S.costed, S.inProgress].includes(st)) return `NTE can't be added to an order that is ${st}.`;
      return row['isCreditCardPayment'] ? "NTE can't be added to a credit card payment." : null;
    case 'enRoute':
      if (![S.open, S.scheduled, S.paused, S.awaitingPayment, S.enroute].includes(st)) return `An order that is ${st} can't be marked En Route.`;
      return st !== S.enroute && ctx.hasEnrouteServiceOrder ? 'You already have an order En Route.' : null;
    case 'checkIn':
      if ([S.completed, S.cancelled, S.pendingApproval, S.importPublishError, S.costed].includes(st)) return `An order that is ${st} can't be checked in or out.`;
      if (ctx.roleClaims['SO_Popup_Fields_checkInDate']?.readOnly || ctx.roleClaims['SO_Popup_Fields_checkOutDate']?.readOnly) return "Your role can't check in or out.";
      return ctx.hasCheckedInServiceOrder && !ctx.isManagerLike && st !== S.inProgress ? 'You are already checked in on another order.' : null;
    case 'claims':
      if (!COMPLETED_STATUSES.includes(st)) return 'Claims are only available on completed orders.';
      return row['claimId'] ? null : 'This order has no claim.';
    case 'invoice':
      if ([S.completed, S.cancelled, S.costed].includes(st)) return `An order that is ${st} can't be invoiced here.`;
      return row['assignmentCount'] && st !== S.pendingApproval ? null : 'Invoicing needs at least one assignment.';
    default:
      return null;
  }
}

// Action icons (raw SVG, library convention), shared with the Dashboard's My Day grid.
export const ACTION_ICON_SVG = {
  details: `<line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />`,
  assignments: `<path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /><polyline points="9 14 11 16 15 12" />`,
  links: `<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />`,
  nte: `<line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />`,
  enRoute: `<rect x="1" y="3" width="15" height="13" /><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" /><circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />`,
  // A person standing on a location ring: the service order popup's check-in button (fa-street-view).
  checkIn: `<circle cx="12" cy="4" r="2" /><path d="M10 8h4a1.5 1.5 0 0 1 1.5 1.5V14H14v4h-4v-4H8.5V9.5A1.5 1.5 0 0 1 10 8z" /><path d="M7 16.6C4.6 17.1 3 18 3 19c0 1.66 4.03 3 9 3s9-1.34 9-3c0-1-1.6-1.9-4-2.4" />`,
  claims: `<path d="M12 1v22" /><path d="M5 9h11a3 3 0 0 1 0 6H8" /><path d="M8 12l-3 3 3 3" />`,
  invoice: `<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" />`,
  attachments: `<path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />`,
  comments: `<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />`,
};

export function buildServiceOrderActionIcons(ctx: ServiceOrderGridContext): ActionIconDef[] {
  const color = (key: string, row: GridRow, enabledColor: string) => (disabledReason(key, row, ctx) ? INACTIVE : enabledColor);
  const icons: ActionIconDef[] = [
    { key: 'details', label: 'Details', colorFn: () => BLACK,
      icon: ACTION_ICON_SVG.details },
    { key: 'assignments', label: 'Assignments', colorFn: row => color('assignments', row, BLACK),
      icon: ACTION_ICON_SVG.assignments },
    { key: 'links', label: 'Links', colorFn: row => (row['linkColorCode'] as string) || ctx.defaultLinkColor || BLACK,
      icon: ACTION_ICON_SVG.links },
  ];
  if (!ctx.isKingslynn && ctx.roleClaims['SO_Grid_Action_NTE']?.visible) {
    icons.push({ key: 'nte', label: 'NTE', colorFn: row => (row['nteAmount'] ? GREEN : color('nte', row, BLACK)),
      icon: ACTION_ICON_SVG.nte });
  }
  icons.push(
    { key: 'enRoute', label: 'En Route', colorFn: row => (status(row) === S.enroute ? GREEN : color('enRoute', row, BLACK)),
      icon: ACTION_ICON_SVG.enRoute },
    { key: 'checkIn', label: 'Check In / Check Out', colorFn: row => (disabledReason('checkIn', row, ctx) ? INACTIVE : status(row) === S.inProgress ? GREEN : BLACK),
      icon: ACTION_ICON_SVG.checkIn },
  );
  if (ctx.roleClaims['SO_Grid_Action_Claims']?.visible) {
    // Claims were only shown on the Completed tab: now usable on completed orders. Greyed on the others, not hidden,
    // so every row keeps the same icon slots (UI issues 04: icons shifted on Canceled rows).
    icons.push({ key: 'claims', label: 'Claims', colorFn: row => (row['claimId'] && !disabledReason('claims', row, ctx) ? GREEN : INACTIVE),
      icon: ACTION_ICON_SVG.claims });
  }
  icons.push(
    { key: 'invoice', label: 'Invoice', colorFn: row => color('invoice', row, BLACK),
      icon: ACTION_ICON_SVG.invoice },
    { key: 'attachments', label: 'Attachments', colorFn: row => (row['attachmentCount'] ? GREEN : BLACK),
      icon: ACTION_ICON_SVG.attachments },
    { key: 'comments', label: 'Comments', colorFn: row => (row['commentCount'] ? ORANGE : BLACK),
      icon: ACTION_ICON_SVG.comments },
  );
  return icons;
}

// Columns from the view that today's grid never shows: kept for the action rules, never in the column picker.
const BACKING_COLUMNS = [
  'soId', 'company', 'offlineOrder', 'customSort', 'technicianId', 'mode', 'approvedAmount', 'nteCheckMessage',
  'serviceDepartment', 'configuration', 'itemCode', 'country', 'priority', 'equipmentStatus', 'createdBy',
  'lastUpdatedBy', 'invoiceId', 'invoiceAmount', 'reasonCode', 'reasonType', 'totalAmount', 'commentCount',
  'attachmentCount', 'linkCount', 'openLinkCount', 'linkColorCode', 'assignmentCount', 'isCreditCardPayment',
  'kingslynnPmtCheck', 'statusSort', 'completedDate', 'callerName', 'callerPhone', 'claimId',
  // Computed by the API for My Day's Overdue rule (RIMS API 94532ad5): never a column here.
  'isOverDue',
];

export function buildServiceOrderGridConfig(ctx: ServiceOrderGridContext, gridName: string): GridConfig {
  const showNte = !ctx.isKingslynn && !!ctx.roleClaims['SO_Grid_Action_NTE']?.visible;
  const order = [
    'serviceOrderId', 'serviceOrderIdLN', 'status', 'createdDate', 'startDate', 'priorityDescription',
    'serviceDepartmentDescription', 'siteName', 'technicianName', 'callgroup', 'callGroupDescription', 'serviceOrderType',
    'itemDescription', 'serialNumber', 'equipmentStatusDescription', 'reasonDescription', 'customerCode',
    'customerDescription', 'address', 'city', 'state', 'zipCode', 'customerPhoneNumber', 'customerArea', 'reference',
    ...(showNte ? ['nteAmount'] : []),
    'referenceActivityDescription', 'lastUpdatedDate', 'effectiveCompletedDate',
    ...(ctx.isKingslynn ? ['calibrationNextCertificateDate'] : []),
  ];
  const hiddenAtStart = [
    'priorityDescription', 'serviceDepartmentDescription', 'callGroupDescription', 'serialNumber', 'customerCode',
    'address', 'city', 'state', 'zipCode', 'customerArea', 'reference', 'referenceActivityDescription',
    'lastUpdatedDate', 'effectiveCompletedDate', ...(ctx.showSOType ? [] : ['serviceOrderType']),
  ];
  return centreStatusColumns({
    apiBaseUrl: `${environment.apiUrl}grid/service-orders`,
    keyColumn: 'soId',
    title: 'Service Orders',
    // Denser rows than the 32px default, to show more orders at once (trial, Martin 2026-10-01).
    rowHeight: 26,
    // One loading indicator, at the bottom of the rows area: "Loading…" for the first page, "Loading more…" after
    // (library 0.3.22; same as the Dashboard popup, Martin 2026-10-14).
    loadingIndicator: 'banner',
    // Chevron in the Actions header: collapsed, only Comments stays (Martin 2026-10-15). Saved per user with
    // the layout (library 0.3.22, LIBRARY-REQUESTS #22).
    collapsibleColumns: { actions: { keep: ['comments'] } },
    defaultColumnOrder: order,
    hiddenByDefaultColumns: [...BACKING_COLUMNS, ...(showNte ? [] : ['nteAmount']), ...(ctx.isKingslynn ? [] : ['calibrationNextCertificateDate'])],
    startHiddenColumns: hiddenAtStart,
    columnLabels: {
      serviceOrderId: 'Order No.', serviceOrderIdLN: 'LN Order No.', status: 'Status', createdDate: 'Created Date',
      startDate: 'Start Date', priorityDescription: 'Priority', serviceDepartmentDescription: 'Service Department',
      siteName: 'Site Name', technicianName: 'Technician Name', callgroup: 'Call Group', callGroupDescription: 'Call Group Description',
      serviceOrderType: 'SO Type', itemDescription: 'Item/Object', serialNumber: 'Serial No.',
      equipmentStatusDescription: 'Equip. Status', reasonDescription: 'Reason', customerCode: 'Customer Id',
      customerDescription: 'Customer', address: 'Address', city: 'City', state: 'State', zipCode: 'Zip Code',
      customerPhoneNumber: 'Telephone', customerArea: 'Customer Area', reference: 'Reference', nteAmount: 'NTE Amount',
      referenceActivityDescription: 'Ref. Activity', lastUpdatedDate: 'Last Updated Date',
      effectiveCompletedDate: 'Completed Date', calibrationNextCertificateDate: 'Calibration Due Date',
    },
    columnWidths: {
      serviceOrderId: 120, serviceOrderIdLN: 120, status: 172, createdDate: 220, startDate: 140, priorityDescription: 180,
      technicianName: 180, callgroup: 120, callGroupDescription: 170, serviceOrderType: 106, itemDescription: 150,
      serialNumber: 150, reasonDescription: 172, address: 150, reference: 120, referenceActivityDescription: 120,
      lastUpdatedDate: 160, effectiveCompletedDate: 160, calibrationNextCertificateDate: 140,
    },
    columnDateFormats: {
      createdDate: 'd MMM, yyyy hh:mm a', startDate: 'd MMM, yyyy', lastUpdatedDate: 'd MMM, yyyy',
      effectiveCompletedDate: 'd MMM, yyyy', calibrationNextCertificateDate: 'd MMM, yyyy',
    },
    // Created Date filters to the minute: the date filter gets a Time toggle with a date-time picker.
    columnTypes: { createdDate: 'datetime' },
    hyperlinkColumns: ['serviceOrderId', 'serviceOrderIdLN', 'customerDescription'],
    excelFilterColumns: [
      'status', 'priorityDescription', 'serviceDepartmentDescription', 'siteName', 'technicianName', 'callgroup',
      'callGroupDescription', 'serviceOrderType', 'equipmentStatusDescription', 'reasonDescription', 'customerCode',
      'city', 'state', 'referenceActivityDescription',
    ],
    columnColors: { status: statusColor },
    // Group by (customer request, 2026-10-02): no list here on purpose. The columns come from the
    // database (ux.GridGroupableColumn, DB-005) through the schema, so they change without a deploy.
    enableContextMenuFilter: true,
    // Excel export, only for roles with both export claims. The prompt asks how many rows (default / max
    // from the application parameters, DB-006) and the grid gathers them itself, not only the loaded pages.
    enableExport: !!(ctx.roleClaims['AllGrid_Toolbar_Excel_Export']?.visible && ctx.roleClaims['SO_Grid_Toolbar_Excel_Export']?.visible),
    exportLimit: ctx.exportLimit,
    enableFitColumns: true,
    // System, shared and personal views all come from ux.UserComponentView (RIMS API api/prefs/views).
    // The system views (Assigned, Completed / Canceled this and last month, All orders) are rows there
    // since DB-003, so they change without a redeploy.
    enableSavedViews: true,
    toolbarActions: [
      ...(ctx.roleClaims['SO_Grid_Toolbar_CreateOrder']?.visible
        ? [{ key: 'add-service-order', label: 'Add Service Order', kind: 'button' as const, icon: '<line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />' }]
        : []),
    ],
    // Reset layout is the grid's own toolbar button (undo icon, last in the toolbar); the page re-applies
    // the Assigned view after it (layoutReset). Multi-word headers wrap between words, as in master data.
    enableResetLayout: true,
    wrapHeaders: true,
    persistenceKey: `rims.service.${gridName}`,
    persistenceVersion: 2, // 2: Site Name column (a saved layout would hide it)
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  });
}
